import { apiService } from './apiService';
import { TeamMember } from '../types';

class TeamMemberService {
  private cache: TeamMember[] | null = null;
  private cacheExpiry: number = 0;
  private readonly CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours
  private readonly STORAGE_KEY = 'team_members_cache';
  private readonly STORAGE_TIMESTAMP_KEY = 'team_members_cache_timestamp';
  private pendingRequest: Promise<TeamMember[]> | null = null;

  // Clear cache
  private clearCache() {
    this.cache = null;
    this.cacheExpiry = 0;
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.STORAGE_TIMESTAMP_KEY);
  }

  // Check if cache is valid
  private isCacheValid(): boolean {
    return this.cache !== null && Date.now() < this.cacheExpiry;
  }

  // Convert database team member to frontend format
  private convertDbTeamMemberToFrontend(dbMember: any): TeamMember {
    return {
      id: dbMember.id,
      name: dbMember.name,
      role: dbMember.role,
      bio: dbMember.bio,
      imageUrl: dbMember.imageUrl || dbMember.image_url || undefined,
      specialties: dbMember.specialties || [],
      email: dbMember.email || undefined,
      phone: dbMember.phone || undefined,
      linkedinUrl: dbMember.linkedin_url || dbMember.linkedinUrl || undefined,
      isFounder: dbMember.is_founder === true || dbMember.isFounder === true,
      sortOrder: dbMember.sort_order || dbMember.sortOrder || 0,
      active: dbMember.active !== false,
      featured: dbMember.featured === true,
      createdAt: dbMember.created_at || dbMember.createdAt || new Date().toISOString(),
      updatedAt: dbMember.updated_at || dbMember.updatedAt || new Date().toISOString()
    };
  }

  // Convert frontend team member to database format
  private convertFrontendTeamMemberToDb(member: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>): any {
    return {
      name: member.name,
      role: member.role,
      bio: member.bio,
      image_url: member.imageUrl || null,
      specialties: member.specialties,
      email: member.email || null,
      phone: member.phone || null,
      linkedin_url: member.linkedinUrl || null,
      is_founder: member.isFounder,
      sort_order: member.sortOrder,
      active: member.active,
      featured: member.featured
    };
  }

  // Load cache from localStorage
  private loadCacheFromStorage(): void {
    try {
      const cachedData = localStorage.getItem(this.STORAGE_KEY);
      const cachedTimestamp = localStorage.getItem(this.STORAGE_TIMESTAMP_KEY);

      if (cachedData && cachedTimestamp) {
        const timestamp = parseInt(cachedTimestamp, 10);
        const now = Date.now();

        if (now - timestamp < this.CACHE_DURATION) {
          this.cache = JSON.parse(cachedData);
          this.cacheExpiry = timestamp;
          // console.log('📦 Loaded team members from localStorage cache');
        }
      }
    } catch (error) {
      console.error('Error loading cache from localStorage:', error);
    }
  }

  // Save cache to localStorage
  private saveCacheToStorage(members: TeamMember[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(members));
      localStorage.setItem(this.STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Error saving cache to localStorage:', error);
    }
  }

  constructor() {
    // Load from localStorage immediately for instant response
    this.loadCacheFromStorage();
    if (this.cache && this.cache.length > 0) {
      // console.log(`🚀 TeamMemberService initialized - loaded ${this.cache.length} members from cache`);
    }
  }

  // Get all team members with enhanced caching
  async getAllTeamMembers(forceRefresh: boolean = false): Promise<TeamMember[]> {
    // Return cached data immediately if valid
    if (!forceRefresh && this.isCacheValid()) {
      // Refresh in background if cache is older than 30 minutes
      const cacheAge = Date.now() - this.cacheExpiry;
      if (cacheAge > 30 * 60 * 1000) {
        this.fetchAllTeamMembersFromDatabase().catch(() => {
          // Silently fail - we have cache
        });
      }
      return [...this.cache!];
    }

    // If cache is empty, fetch from database
    if (!this.cache || this.cache.length === 0) {
      if (this.pendingRequest) {
        return await this.pendingRequest;
      }

      this.pendingRequest = this.fetchAllTeamMembersFromDatabase();
      try {
        const members = await this.pendingRequest;
        this.pendingRequest = null;
        return members;
      } catch (error) {
        this.pendingRequest = null;
        throw error;
      }
    }

    // Cache expired - fetch fresh data
    return await this.fetchAllTeamMembersFromDatabase();
  }

  // Fetch all team members from database
  private async fetchAllTeamMembersFromDatabase(): Promise<TeamMember[]> {
    try {
      const data = await apiService.request<any[]>('/team-members');

      const members = (data || []).map((dbMember: any) => this.convertDbTeamMemberToFrontend(dbMember));

      // Update cache
      this.cache = members;
      this.cacheExpiry = Date.now();
      this.saveCacheToStorage(members);

      return members;
    } catch (error) {
      console.error('API error fetching team members:', error);
      return this.cache || [];
    }
  }

  // Get active team members (for About page display) - uses cache
  async getActiveTeamMembers(forceRefresh: boolean = false): Promise<TeamMember[]> {
    // Use cached data if available, otherwise fetch
    const allMembers = await this.getAllTeamMembers(forceRefresh);
    return allMembers.filter(member => member.active);
  }

  // Get team member by ID
  async getTeamMemberById(id: string): Promise<TeamMember | null> {
    // Check cache first
    if (this.cache && this.cache.length > 0) {
      const cached = this.cache.find(m => m.id === id);
      if (cached) {
        // Revalidate in background
        this.fetchAllTeamMembersFromDatabase().catch(() => { });
        return cached;
      }
    }

    try {
      const data = await apiService.request<any>(`/team-members/${id}`);
      const member = this.convertDbTeamMemberToFrontend(data);
      
      // Update cache with this specific member if not already there
      if (this.cache) {
        const idx = this.cache.findIndex(m => m.id === id);
        if (idx !== -1) this.cache[idx] = member;
        else this.cache.push(member);
        this.saveCacheToStorage(this.cache);
      }
      
      return member;
    } catch (error) {
      console.error('API error fetching team member by ID:', error);
      return null;
    }
  }

  // Add new team member
  async addTeamMember(memberData: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>): Promise<TeamMember | null> {
    try {
      // Prevent adding multiple founders
      if (memberData.isFounder) {
        const existingFounder = await this.getFounder();
        if (existingFounder) {
          console.error('Cannot add multiple founders');
          return null;
        }
      }

      const dbMember = this.convertFrontendTeamMemberToDb(memberData);

      const data = await apiService.request<any>('/team-members', {
        method: 'POST',
        body: JSON.stringify(dbMember)
      });

      this.clearCache();

      return this.convertDbTeamMemberToFrontend(data);
    } catch (error) {
      console.error('API error adding team member:', error);
      return null;
    }
  }

  // Update team member
  async updateTeamMember(id: string, memberData: Partial<Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>>): Promise<TeamMember | null> {
    try {
      // Prevent updating founder's is_founder status
      const existingMember = await this.getTeamMemberById(id);
      if (existingMember?.isFounder && memberData.isFounder === false) {
        console.error('Cannot change founder status');
        return null;
      }

      const updateData: any = {};

      if (memberData.name !== undefined) updateData.name = memberData.name;
      if (memberData.role !== undefined) updateData.role = memberData.role;
      if (memberData.bio !== undefined) updateData.bio = memberData.bio;
      if (memberData.imageUrl !== undefined) updateData.image_url = memberData.imageUrl;
      if (memberData.specialties !== undefined) updateData.specialties = memberData.specialties;
      if (memberData.email !== undefined) updateData.email = memberData.email;
      if (memberData.phone !== undefined) updateData.phone = memberData.phone;
      if (memberData.linkedinUrl !== undefined) updateData.linkedin_url = memberData.linkedinUrl;
      if (memberData.isFounder !== undefined && !existingMember?.isFounder) updateData.is_founder = memberData.isFounder;
      if (memberData.sortOrder !== undefined) updateData.sort_order = memberData.sortOrder;
      if (memberData.active !== undefined) updateData.active = memberData.active;
      if (memberData.featured !== undefined) updateData.featured = memberData.featured;

      const data = await apiService.request<any>(`/team-members/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      this.clearCache();

      return this.convertDbTeamMemberToFrontend(data);
    } catch (error) {
      console.error('API error updating team member:', error);
      return null;
    }
  }

  // Delete team member
  async deleteTeamMember(id: string): Promise<boolean> {
    try {
      // Check if it's the founder
      const member = await this.getTeamMemberById(id);
      if (member?.isFounder) {
        console.error('Cannot delete founder');
        return false;
      }

      await apiService.request(`/team-members/${id}`, { method: 'DELETE' });

      this.clearCache();
      return true;
    } catch (error) {
      console.error('API error deleting team member:', error);
      return false;
    }
  }

  // Get founder
  async getFounder(): Promise<TeamMember | null> {
    try {
      const members = await this.getAllTeamMembers();
      return members.find(m => m.isFounder) || null;
    } catch (error) {
      console.error('API error fetching founder:', error);
      return null;
    }
  }

  // Toggle team member active status
  async toggleTeamMemberActive(id: string): Promise<boolean> {
    try {
      const member = await this.getTeamMemberById(id);
      if (member?.isFounder) {
        console.error('Cannot deactivate founder');
        return false;
      }

      if (!member) return false;

      await this.updateTeamMember(id, { active: !member.active });

      return true;
    } catch (error) {
      console.error('API error toggling team member active status:', error);
      return false;
    }
  }

  // Toggle team member featured status
  async toggleTeamMemberFeatured(id: string): Promise<boolean> {
    try {
      const member = await this.getTeamMemberById(id);
      if (!member) return false;

      await this.updateTeamMember(id, { featured: !member.featured });

      return true;
    } catch (error) {
      console.error('API error toggling team member featured status:', error);
      return false;
    }
  }

  // Reorder team members
  async reorderTeamMembers(updates: { id: string; sortOrder: number }[]): Promise<boolean> {
    try {
      await apiService.request('/team-members/reorder', {
        method: 'POST',
        body: JSON.stringify(updates)
      });

      this.clearCache();
      return true;
    } catch (error) {
      console.error('API error reordering team members:', error);
      return false;
    }
  }

  // Search team members
  async searchTeamMembers(query: string): Promise<TeamMember[]> {
    try {
      const lowerQuery = query.toLowerCase();
      const allMembers = await this.getActiveTeamMembers();

      return allMembers.filter(member =>
        member.name.toLowerCase().includes(lowerQuery) ||
        member.role.toLowerCase().includes(lowerQuery) ||
        (member.bio && member.bio.toLowerCase().includes(lowerQuery))
      ).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    } catch (error) {
      console.error('API error searching team members:', error);
      return [];
    }
  }

  // Get team member statistics
  async getTeamMemberStats(): Promise<{ total: number; active: number; featured: number; founders: number }> {
    try {
      const allMembers = await this.getAllTeamMembers();

      return {
        total: allMembers.length,
        active: allMembers.filter(m => m.active).length,
        featured: allMembers.filter(m => m.featured).length,
        founders: allMembers.filter(m => m.isFounder).length
      };
    } catch (error) {
      console.error('API error fetching team member stats:', error);
      return { total: 0, active: 0, featured: 0, founders: 0 };
    }
  }
}

export const teamMemberService = new TeamMemberService();
